<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useSlugAvailability } from '@/composition/store-setup/use-slug-availability';
  import type { TSlugState } from '@/composition/store-setup/use-slug-availability';
  import {
    followedSlug,
    slugDisplayState,
    slugDisplaySuggestions,
  } from '@/domain/store-setup/business-step';
  import type { ISlugConflict } from '@/domain/store-setup/business-step';
  import { SLUG_MAX, sanitizeSlugInput } from '@/domain/store-setup/store-slug';
  import { storeLinkLabel } from '@/domain/store-setup/store-identity';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Su enlace: bistri.fi/laesquinadetito» (plan §4.2 item 2, D4). The link
   * follows the name until the owner edits it («Cambiar») or adopts a
   * suggestion; its availability is checked 400 ms after every change.
   */
  /** «bistri.fi/» */
  const LINK_PREFIX = storeLinkLabel('');
  /** The line under the link for the states that need words. */
  const STATE_MESSAGES: Partial<Record<TSlugState, string>> = {
    taken: 'storeSetup.business.linkTaken',
    invalid: 'storeSetup.business.linkRules',
    short: 'storeSetup.business.linkShort',
    unchecked: 'storeSetup.business.linkUnchecked',
  };

  const props = withDefaults(
    defineProps<{
      name: string;
      city?: string | null;
      value: string;
      edited: boolean;
      disabled?: boolean;
      /** The link the create call refused twice: shown taken with the links it offered. */
      conflict?: ISlugConflict | null;
    }>(),
    { city: null, disabled: false, conflict: null }
  );
  const emit = defineEmits<{
    (e: 'input', slug: string): void;
    (e: 'edited'): void;
    (e: 'state', state: TSlugState): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const availability = useSlugAvailability(() => ({
    name: props.name,
    city: props.city,
    slug: props.value,
    edited: props.edited,
  }));
  const editing = ref(false);
  const root = ref<HTMLElement | null>(null);
  const inputId = nextDomId('setup-slug-input');
  const rulesId = nextDomId('setup-slug-rules');

  const state = computed(() =>
    slugDisplayState(availability.state.value, props.value, props.conflict)
  );
  const suggestions = computed(() =>
    slugDisplaySuggestions(
      availability.suggestions.value,
      props.value,
      props.conflict
    )
  );
  const hasAlternatives = computed(
    () => state.value === 'taken' || state.value === 'invalid'
  );
  const message = computed(() => {
    const key = STATE_MESSAGES[state.value];
    return key ? tr(key) : '';
  });
  /** Only a failed check is neutral: it never blocks. */
  const isError = computed(() => state.value !== 'unchecked');

  function focusIn(selector: string) {
    const element = root.value?.querySelector(selector);
    if (element instanceof HTMLElement) element.focus({ preventScroll: true });
  }

  function markEdited() {
    if (!props.edited) emit('edited');
  }

  async function openEditor() {
    if (props.disabled) return;
    editing.value = true;
    await nextTick();
    focusIn('.slug-editor input');
  }

  async function closeEditor() {
    editing.value = false;
    await nextTick();
    focusIn('.slug-change');
  }

  /** Only `[a-z0-9]`, lower case, no accents, as the owner types. */
  function onEditInput(raw: string) {
    const clean = sanitizeSlugInput(raw);
    // The prop may not change (a typed space): put the clean text back in the box.
    const input = root.value?.querySelector('.slug-editor input');
    if (input instanceof HTMLInputElement && input.value !== clean)
      input.value = clean;
    markEdited();
    if (clean !== props.value) emit('input', clean);
  }

  function adopt(slug: string) {
    if (props.disabled) return;
    markEdited();
    emit('input', slug);
  }

  // Until edited, the link follows the name (and the city, for short names).
  watch(
    () => [props.name, props.city, props.edited] as const,
    () => {
      if (props.edited) return;
      const next = followedSlug(props.name, props.city);
      if (next !== props.value) emit('input', next);
    },
    { immediate: true }
  );
  watch(state, (value) => emit('state', value), { immediate: true });
</script>

<template>
  <div ref="root" class="v2-setup-slug-field">
    <div class="slug-line">
      <span class="slug-label">{{ $t('storeSetup.business.link') }}</span>
      <span class="slug-url"
        >{{ LINK_PREFIX }}<span v-if="value">{{ value }}</span
        ><span v-else class="slug-empty">…</span></span
      >
      <span class="slug-inline" aria-live="polite">
        <span v-if="state === 'checking'" class="slug-status is-checking">
          <span class="status-spinner" aria-hidden="true" />
          {{ $t('storeSetup.business.linkChecking') }}
        </span>
        <span
          v-else-if="state === 'available'"
          class="slug-status is-available"
        >
          <PosUiIcon name="check" class="status-icon" />
          {{ $t('storeSetup.business.linkAvailable') }}
        </span>
      </span>
      <button
        v-show="!editing"
        type="button"
        class="slug-change"
        :disabled="disabled"
        @click="openEditor"
      >
        {{ $t('storeSetup.business.linkChange') }}
      </button>
    </div>
    <div v-show="editing" class="slug-editor">
      <div class="editor-row">
        <PosUiInput
          :id="inputId"
          class="editor-input"
          :value="value"
          :prefix="LINK_PREFIX"
          :maxlength="SLUG_MAX"
          autocomplete="off"
          :aria-label="$t('storeSetup.business.link')"
          :aria-describedby="rulesId"
          :disabled="disabled"
          @input="onEditInput"
          @enter="closeEditor"
        />
        <PosUiButton
          class="editor-done"
          color="neutral"
          variant="outline"
          :disabled="disabled"
          @click="closeEditor"
          >{{ $t('storeSetup.business.linkDone') }}</PosUiButton
        >
      </div>
      <p v-show="state !== 'invalid'" :id="rulesId" class="slug-hint">
        {{ $t('storeSetup.business.linkRules') }}
      </p>
    </div>
    <div class="slug-messages" role="status" aria-live="polite">
      <p v-if="message" class="slug-message" :class="{ 'is-error': isError }">
        {{ message }}
      </p>
    </div>
    <div v-if="hasAlternatives && suggestions.length" class="suggestion-chips">
      <PosUiChip
        v-for="suggestion in suggestions"
        :key="suggestion"
        :disabled="disabled"
        :aria-label="storeLinkLabel(suggestion)"
        @click="adopt(suggestion)"
        >{{ suggestion }}</PosUiChip
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-slug-field {
    @apply tw-flex tw-min-w-0 tw-flex-col;
    .slug-line {
      @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm;
    }
    .slug-label {
      @apply tw-text-neutral-500;
    }
    .slug-url {
      @apply tw-min-w-0 tw-font-semibold tw-text-neutral-900;
      overflow-wrap: anywhere;
    }
    .slug-empty {
      @apply tw-font-normal tw-text-neutral-400;
    }
    .slug-status {
      @apply tw-inline-flex tw-items-center tw-gap-1 tw-text-xs;
      &.is-checking {
        @apply tw-text-neutral-500;
      }
      &.is-available {
        @apply tw-font-medium tw-text-emerald-700;
      }
    }
    .slug-inline {
      @apply tw-inline-flex;
    }
    .status-icon {
      @apply tw-text-sm;
    }
    .status-spinner {
      @apply tw-h-3 tw-w-3 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400 tw-border-t-transparent;
    }
    .slug-change {
      @apply tw-min-h-11 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .slug-editor {
      @apply tw-mt-1 tw-flex tw-flex-col tw-gap-1.5;
    }
    .editor-row {
      @apply tw-flex tw-items-center tw-gap-2;
    }
    .editor-input {
      @apply tw-min-w-0 tw-flex-1;
    }
    .editor-done {
      @apply tw-shrink-0;
    }
    .slug-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    // Always in the page (a live region must exist before it speaks); no size while empty.
    .slug-messages {
      @apply tw-flex tw-flex-col;
    }
    .slug-message {
      @apply tw-m-0 tw-mt-1 tw-text-xs tw-text-neutral-500;
      &.is-error {
        @apply tw-text-red-600;
      }
    }
    .suggestion-chips {
      @apply tw-mt-2 tw-flex tw-flex-wrap tw-gap-2;
    }
  }
</style>
